import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { GridAutosizeOptions, GridColDef } from '@mui/x-data-grid';
import { isNumber, isPlainObject, isString, kebabCase } from 'es-toolkit';
import { compact, every, filter, find, first, includes, keys, map, some, sortBy, uniq } from 'es-toolkit/compat';
import React from 'react';

import { getReadOnlyDateTimeFilterOperators } from 'components/atoms/BaseDataGrid/ReadOnlyDataGrid';
import { SlidePreviewCell } from 'components/atoms/DataGridRenderers/SlideIdCellRenderer';
import { SectionTable, TableColumn } from 'interfaces/agentSession';
import { Slide } from 'interfaces/slide';
import { toDate } from 'utils/dateTime';
import {
  JobResultResolvedValue as QaResolvedValue,
  JobResultTableAugmentation as StudyReportTableAugmentation,
} from 'utils/jobResultAugmentation';
import { resolveOriginTemplate } from 'utils/resolveOriginTemplate';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useNavigationToViewerPage } from 'utils/useNavigationToViewerPage';
import { buildStudyReportViewerUrl, StudyReportViewerLinkResolver } from './studyReportViewerLinks';

const qaValueChipSx = {
  maxWidth: '100%',
  '& .MuiChip-label': {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  },
};

const buildQaValueChipLabel = ({ label, color }: { label: string; color?: string }) =>
  React.createElement(
    Box,
    {
      component: 'span',
      sx: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        maxWidth: '100%',
      },
    },
    color
      ? React.createElement(Box, {
          component: 'span',
          sx: {
            width: 8,
            height: 8,
            borderRadius: '50%',
            flexShrink: 0,
            backgroundColor: color,
          },
        })
      : null,
    React.createElement(
      Box,
      {
        component: 'span',
        sx: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'text.primary',
          fontWeight: 600,
        },
      },
      label
    )
  );

const SLIDE_FIELD_NAME_PATTERN = /^slideids?(?:\d+)?$/;
const ROW_IDENTIFIER_FIELD_NAMES = [
  'id',
  'slideId',
  'slide_id',
  'caseId',
  'case_id',
  'studyId',
  'study_id',
  'sampleId',
  'sample_id',
  'itemId',
  'item_id',
  'artifactId',
  'artifact_id',
];
const INDEX_FIELD_NAMES = ['#', 'index', 'rowIndex', 'row_index', 'rowNumber', 'row_number', 'rank', 'position'];
const MARKDOWN_LINK_PATTERN = /^\[([^\]]+)\]\(((?:https?:\/\/|\{\{origin\}\})[^\s)]+)\)$/i;
const URL_PATTERN = /^(?:https?:\/\/|\{\{origin\}\})\S+$/i;
const CSV_QUOTE_REGEX = /"/g;

interface NormalizedTableLinkValue {
  href: string;
  label: string;
}

const isPrimitiveCellValue = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

const normalizeTableLinkValue = (value: unknown): NormalizedTableLinkValue | null => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    const markdownMatch = trimmedValue.match(MARKDOWN_LINK_PATTERN);
    if (markdownMatch) {
      return {
        href: resolveOriginTemplate(markdownMatch[2]),
        label: markdownMatch[1],
      };
    }

    if (URL_PATTERN.test(trimmedValue)) {
      return {
        href: resolveOriginTemplate(trimmedValue),
        label: resolveOriginTemplate(trimmedValue),
      };
    }
  }

  if (!isPlainObject(value)) {
    return null;
  }

  const maybeHref = (value as { href?: unknown; url?: unknown }).href || (value as { url?: unknown }).url;
  if (typeof maybeHref !== 'string' || !URL_PATTERN.test(maybeHref)) {
    return null;
  }

  const resolvedHref = resolveOriginTemplate(maybeHref);
  const maybeLabel =
    (value as { label?: unknown; text?: unknown; title?: unknown }).label ||
    (value as { text?: unknown }).text ||
    (value as { title?: unknown }).title;

  return {
    href: resolvedHref,
    label: typeof maybeLabel === 'string' && maybeLabel.trim() ? maybeLabel : resolvedHref,
  };
};

const getResolvedQaValue = ({
  column,
  value,
  augmentation,
}: {
  column: TableColumn;
  value: unknown;
  augmentation?: StudyReportTableAugmentation;
}) => {
  if (!augmentation) {
    return null;
  }

  const columnMeta = column.meta || augmentation.columnMetaByField[column.field];
  if (!columnMeta) {
    return null;
  }

  return augmentation.resolveColumnValue(column.field, value);
};

const stopTableCellLinkPropagation = (event: React.MouseEvent<HTMLElement>) => event.stopPropagation();

const getTableCellFilterValue = ({
  value,
  column,
  augmentation,
}: {
  value: unknown;
  column?: TableColumn;
  augmentation?: StudyReportTableAugmentation;
}): unknown => {
  if (column) {
    const resolvedQaValue = getResolvedQaValue({ column, value, augmentation });
    if (resolvedQaValue?.text) {
      return resolvedQaValue.text;
    }
  }

  const normalizedLink = normalizeTableLinkValue(value);
  if (normalizedLink) {
    return normalizedLink.label;
  }

  if (value instanceof Date) {
    return value;
  }

  if (isPrimitiveCellValue(value)) {
    return value;
  }

  return value ?? null;
};

const NORMALIZATION_REGEX = /[\s-]+/g;

const normalizeFieldName = (value?: string | null) => (value || '').replace(NORMALIZATION_REGEX, '').toLowerCase();

const getColumnEntityType = ({
  column,
  augmentation,
}: {
  column: TableColumn;
  augmentation?: StudyReportTableAugmentation;
}) => column.meta?.entityType || augmentation?.columnMetaByField[column.field]?.entityType;

const isSlideFieldName = (value?: string | null) => SLIDE_FIELD_NAME_PATTERN.test(normalizeFieldName(value));

const ID_FIELD_REGEX = /(^|_)?id$/i;
const ID_FIELD_SUFFIX_REGEX = /Id$/;

const resolvePreferredRowIdField = (table: SectionTable) => {
  const columnFields = map(table.columns, (column) => column.field);
  const rowFieldNames = keys((first(table.rows) as Record<string, unknown>) || {});
  const candidateFields = uniq([...columnFields, ...rowFieldNames]);
  const firstMatchingKnownField = find(ROW_IDENTIFIER_FIELD_NAMES, (fieldName) => includes(candidateFields, fieldName));
  if (firstMatchingKnownField) {
    return firstMatchingKnownField;
  }

  return find(candidateFields, (fieldName) => ID_FIELD_REGEX.test(fieldName) || ID_FIELD_SUFFIX_REGEX.test(fieldName));
};

const isStablePrimitiveRowId = (value: unknown): value is string | number => isString(value) || isNumber(value);

const resolveStableRowIdField = (table: SectionTable) => {
  const candidateField = resolvePreferredRowIdField(table);

  if (!candidateField) {
    return undefined;
  }

  const seenRowIds = new Set<string>();

  const hasUniqueValuesForField = every(table.rows, (row) => {
    const rawValue = (row as Record<string, unknown>)[candidateField];
    if (!isStablePrimitiveRowId(rawValue)) {
      return false;
    }

    const encodedValue = `${typeof rawValue}:${String(rawValue)}`;
    if (seenRowIds.has(encodedValue)) {
      return false;
    }

    seenRowIds.add(encodedValue);
    return true;
  });

  return hasUniqueValuesForField ? candidateField : undefined;
};

const shouldHideIndexColumn = (table: SectionTable, column: TableColumn) => {
  const preferredRowIdField = resolvePreferredRowIdField(table);
  if (!preferredRowIdField) {
    return false;
  }

  const normalizedField = normalizeFieldName(column.field);
  const normalizedHeader = normalizeFieldName(column.headerName);

  return (
    INDEX_FIELD_NAMES.some((fieldName) => normalizedField === normalizeFieldName(fieldName)) ||
    INDEX_FIELD_NAMES.some((fieldName) => normalizedHeader === normalizeFieldName(fieldName))
  );
};

const resolveCategoricalValueOptions = (
  table: SectionTable,
  column: TableColumn,
  augmentation?: StudyReportTableAugmentation
) => {
  if (column.valueOptions && column.valueOptions.length > 0) {
    return column.valueOptions;
  }

  if (column.type && ['number', 'boolean', 'date', 'dateTime'].includes(column.type)) {
    return undefined;
  }

  const collectedValues = compact(
    map(table.rows, (row) => {
      const cellValue = getTableCellFilterValue({
        value: (row as Record<string, unknown>)[column.field],
        column,
        augmentation,
      });
      return isPrimitiveCellValue(cellValue) ? String(cellValue) : null;
    })
  );
  const uniqueValues: string[] = [];

  for (const value of collectedValues) {
    if (!includes(uniqueValues, value)) {
      uniqueValues.push(value);
    }
  }

  const sortedUniqueValues = sortBy(uniqueValues, (value) => value.toLowerCase());

  if (sortedUniqueValues.length < 2) {
    return undefined;
  }

  return sortedUniqueValues;
};

const buildColumnValueGetter = (column: TableColumn, augmentation?: StudyReportTableAugmentation) => {
  if (column.type === 'date' || column.type === 'dateTime') {
    return (value: unknown) => {
      const filterValue = getTableCellFilterValue({ value, column, augmentation });
      if (filterValue === null || filterValue === undefined || filterValue === '') {
        return null;
      }
      if (filterValue instanceof Date) {
        return filterValue;
      }
      if (typeof filterValue === 'string' || typeof filterValue === 'number') {
        const parsedDate = toDate(filterValue);
        return parsedDate || filterValue;
      }
      return filterValue;
    };
  }

  return (value: unknown) => getTableCellFilterValue({ value, column, augmentation });
};

const getSlidePreviewIds = ({ rawValue, column }: { rawValue: unknown; column: TableColumn }) => {
  if (Array.isArray(rawValue)) {
    return compact(
      rawValue.map((item) => {
        if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
          return String(item).trim();
        }

        return '';
      })
    );
  }

  if (column.meta?.serializedAs === 'comma_separated_string' && typeof rawValue === 'string') {
    return compact(rawValue.split(column.meta.separator || ', ').map((item) => item.trim()));
  }

  if (typeof rawValue === 'string' || typeof rawValue === 'number' || typeof rawValue === 'boolean') {
    return compact([String(rawValue).trim()]);
  }

  return [];
};

const isSlidePreviewColumn = (column: TableColumn) =>
  column.meta?.entityType === 'slide' || isSlideFieldName(column.field);

const shouldRenderResolvedQaValueAsChips = ({
  column,
  augmentation,
  resolvedQaValue,
}: {
  column: TableColumn;
  augmentation?: StudyReportTableAugmentation;
  resolvedQaValue: QaResolvedValue;
}) => {
  const entityType = getColumnEntityType({ column, augmentation });

  return (
    isSlidePreviewColumn(column) ||
    entityType === 'marker' ||
    entityType === 'cellType' ||
    resolvedQaValue.items.length > 1 ||
    Boolean(resolvedQaValue.color)
  );
};

const buildRenderableQaValue = ({
  resolvedQaValue,
  fallbackLabel,
}: {
  resolvedQaValue?: QaResolvedValue | null;
  fallbackLabel: string;
}): QaResolvedValue => {
  if (resolvedQaValue) {
    return resolvedQaValue;
  }

  return {
    text: fallbackLabel,
    items: [{ rawValue: fallbackLabel, displayValue: fallbackLabel }],
    isAugmented: false,
  };
};

const buildQaValueChip = ({
  field,
  displayValue,
  rawValue,
  color,
  href,
}: {
  field: string;
  displayValue: string;
  rawValue: string;
  color?: string;
  href?: string;
}) =>
  React.createElement(Chip, {
    key: `${field}-${rawValue}-${displayValue}`,
    label: buildQaValueChipLabel({
      label: displayValue,
      color,
    }),
    size: 'small',
    variant: 'outlined',
    clickable: Boolean(href),
    ...(href
      ? {
          component: 'a',
          href,
          target: '_blank',
          rel: 'noopener noreferrer',
          onClick: stopTableCellLinkPropagation,
        }
      : {}),
    sx: qaValueChipSx,
  });

const renderQaValueChips = ({
  field,
  resolvedQaValue,
  href,
}: {
  field: string;
  resolvedQaValue: QaResolvedValue;
  href?: string;
}) => {
  const renderChip = ({ rawValue, displayValue, color }: QaResolvedValue['items'][number], index: number) =>
    buildQaValueChip({
      field: `${field}-${index}`,
      rawValue,
      displayValue,
      color,
      href: resolvedQaValue.items.length === 1 ? href : undefined,
    });

  if (resolvedQaValue.items.length === 1) {
    return renderChip(resolvedQaValue.items[0], 0);
  }

  return React.createElement(
    Stack,
    {
      direction: 'row',
      spacing: 0.5,
      useFlexGap: true,
      sx: { alignItems: 'center', flexWrap: 'wrap', minHeight: 40, py: 0.5 },
    },
    resolvedQaValue.items.map(renderChip)
  );
};

const resolveNumericCellValue = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }

  return undefined;
};

const StudyReportFallbackSlidePreviewCell: React.FC<{
  row: Record<string, unknown>;
  slideIds: string[];
  summaryLabel?: string;
}> = ({ row, slideIds, summaryLabel }) => {
  const { labId: currentLabId } = useCurrentLabId();
  const { getUrlToCasePage, getUrlToSlidePage } = useNavigationToViewerPage();
  const resolvedLabId = typeof row.labId === 'string' ? row.labId : currentLabId;
  const caseId = resolveNumericCellValue(row.caseId);
  const caseStudyId = typeof row.studyId === 'string' ? row.studyId : undefined;
  const slides = React.useMemo(
    () =>
      slideIds.map(
        (slideId) =>
          ({
            ...(row as Partial<Slide>),
            id: slideId,
            labId: resolvedLabId,
          } as Slide)
      ),
    [resolvedLabId, row, slideIds]
  );
  const primarySlide = first(slides);
  const inlineViewerHref = React.useMemo(() => {
    if (!primarySlide || slideIds.length !== 1) {
      return undefined;
    }

    const baseViewerUrl = caseId
      ? getUrlToCasePage({
          caseId,
          caseStudyId,
          labId: resolvedLabId,
          slideIdForReviewing: primarySlide.id,
          selectedSlideIds: [primarySlide.id],
          viewersStainPreferences: primarySlide.stainingType ? [primarySlide.stainingType] : undefined,
        })
      : getUrlToSlidePage({
          slideId: primarySlide.id,
          caseStudyId,
          labId: resolvedLabId,
          selectedSlideIds: [primarySlide.id],
        });

    return buildStudyReportViewerUrl({ baseViewerUrl });
  }, [caseId, caseStudyId, getUrlToCasePage, getUrlToSlidePage, primarySlide, resolvedLabId, slideIds.length]);
  const summaryContent =
    inlineViewerHref && summaryLabel && primarySlide
      ? buildQaValueChip({
          field: 'fallback-slide-preview',
          rawValue: primarySlide.id,
          displayValue: summaryLabel,
          href: inlineViewerHref,
        })
      : undefined;

  return React.createElement(SlidePreviewCell, {
    slides,
    caseId,
    caseStudyId,
    labId: resolvedLabId,
    summaryLabel: summaryContent ? undefined : summaryLabel,
    summaryContent,
    openViewerLink:
      inlineViewerHref && primarySlide
        ? {
            href: inlineViewerHref,
            label: caseId ? 'Open case in viewer' : 'Open slide in viewer',
            target: '_blank',
            rel: 'noopener noreferrer',
          }
        : undefined,
  });
};

const buildLinkCellRenderer = (field: string) =>
  function renderLinkCell(params: {
    row: Record<string, unknown>;
    formattedValue?: unknown;
    value?: unknown;
  }): React.ReactNode {
    const rawValue = params.row[field];
    const normalizedLink = normalizeTableLinkValue(rawValue);
    if (!normalizedLink) {
      return typeof params.formattedValue === 'string' || typeof params.formattedValue === 'number'
        ? String(params.formattedValue)
        : typeof params.value === 'string' || typeof params.value === 'number'
        ? String(params.value)
        : '';
    }

    return React.createElement(
      Link,
      {
        href: normalizedLink.href,
        target: '_blank',
        rel: 'noopener noreferrer',
        underline: 'hover',
        onClick: (event: React.MouseEvent<HTMLAnchorElement>) => event.stopPropagation(),
      },
      normalizedLink.label
    );
  };

const resolveRenderedCellLabel = ({
  rawValue,
  formattedValue,
  value,
  column,
  augmentation,
}: {
  rawValue: unknown;
  formattedValue?: unknown;
  value?: unknown;
  column: TableColumn;
  augmentation?: StudyReportTableAugmentation;
}) => {
  const resolvedQaValue = getResolvedQaValue({ column, value: rawValue, augmentation });
  if (resolvedQaValue?.text) {
    return resolvedQaValue.text;
  }

  if (typeof formattedValue === 'string' || typeof formattedValue === 'number') {
    return String(formattedValue);
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  const filterValue = getTableCellFilterValue({ value: rawValue, column, augmentation });

  return typeof filterValue === 'string' || typeof filterValue === 'number' ? String(filterValue) : '';
};

const buildViewerLinkCellRenderer = ({
  field,
  column,
  augmentation,
  viewerLinkResolver,
}: {
  field: string;
  column: TableColumn;
  augmentation?: StudyReportTableAugmentation;
  viewerLinkResolver: StudyReportViewerLinkResolver;
}) =>
  function renderViewerLinkCell(params: {
    row: Record<string, unknown>;
    formattedValue?: unknown;
    value?: unknown;
  }): React.ReactNode {
    const rawValue = params.row[field];
    const resolvedQaValue = getResolvedQaValue({ column, value: rawValue, augmentation });
    const renderedCellLabel = resolveRenderedCellLabel({
      rawValue,
      formattedValue: params.formattedValue,
      value: params.value,
      column,
      augmentation,
    });
    const resolvedViewerLink = viewerLinkResolver.resolveCellLink({
      column,
      field,
      row: params.row,
      value: rawValue,
      formattedValue: params.formattedValue,
    });
    const chipRenderableValue = buildRenderableQaValue({
      resolvedQaValue,
      fallbackLabel: renderedCellLabel || resolvedViewerLink?.label || '',
    });
    const shouldRenderViewerLinkAsChip =
      isSlidePreviewColumn(column) ||
      (resolvedQaValue ? shouldRenderResolvedQaValueAsChips({ column, augmentation, resolvedQaValue }) : false);
    const linkedChipContent = shouldRenderViewerLinkAsChip
      ? renderQaValueChips({
          field,
          resolvedQaValue: chipRenderableValue,
          href: resolvedViewerLink?.href,
        })
      : null;

    if (!resolvedViewerLink) {
      if (resolvedQaValue && shouldRenderResolvedQaValueAsChips({ column, augmentation, resolvedQaValue })) {
        return renderQaValueChips({ field, resolvedQaValue });
      }

      return resolveRenderedCellLabel({
        rawValue,
        formattedValue: params.formattedValue,
        value: params.value,
        column,
        augmentation,
      });
    }

    const linkLabel = resolvedViewerLink.label || renderedCellLabel;

    if (resolvedViewerLink.previewSlide) {
      return React.createElement(SlidePreviewCell, {
        slides: [resolvedViewerLink.previewSlide],
        labId: resolvedViewerLink.previewSlide.labId,
        caseStudyId: typeof params.row.studyId === 'string' ? params.row.studyId : undefined,
        summaryLabel: linkedChipContent ? undefined : renderedCellLabel,
        summaryContent: linkedChipContent || undefined,
        openViewerLink: {
          href: resolvedViewerLink.href,
          label: resolvedViewerLink.actionLabel || 'Open slide in viewer',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      });
    }

    if (linkedChipContent) {
      return linkedChipContent;
    }

    return React.createElement(
      Link,
      {
        href: resolvedViewerLink.href,
        target: '_blank',
        rel: 'noopener noreferrer',
        underline: 'hover',
        onClick: (event: React.MouseEvent<HTMLAnchorElement>) => event.stopPropagation(),
      },
      linkLabel
    );
  };

const buildFallbackSlidePreviewCellRenderer = ({
  field,
  column,
  augmentation,
}: {
  field: string;
  column: TableColumn;
  augmentation?: StudyReportTableAugmentation;
}) =>
  function renderFallbackSlidePreviewCell(params: {
    row: Record<string, unknown>;
    formattedValue?: unknown;
    value?: unknown;
  }): React.ReactNode {
    const rawValue = params.row[field];
    const slideIds = getSlidePreviewIds({ rawValue, column });

    if (!slideIds.length) {
      return resolveRenderedCellLabel({
        rawValue,
        formattedValue: params.formattedValue,
        value: params.value,
        column,
        augmentation,
      });
    }

    const renderedCellLabel = resolveRenderedCellLabel({
      rawValue,
      formattedValue: params.formattedValue,
      value: params.value,
      column,
      augmentation,
    });

    return React.createElement(StudyReportFallbackSlidePreviewCell, {
      row: params.row,
      slideIds,
      summaryLabel: slideIds.length === 1 ? renderedCellLabel : undefined,
    });
  };

const buildAugmentedCellRenderer = ({
  field,
  column,
  augmentation,
}: {
  field: string;
  column: TableColumn;
  augmentation: StudyReportTableAugmentation;
}) =>
  function renderAugmentedCell(params: {
    row: Record<string, unknown>;
    formattedValue?: unknown;
    value?: unknown;
  }): React.ReactNode {
    const rawValue = params.row[field];
    const resolvedQaValue = augmentation.resolveColumnValue(field, rawValue);

    if (shouldRenderResolvedQaValueAsChips({ column, augmentation, resolvedQaValue })) {
      return renderQaValueChips({ field, resolvedQaValue });
    }

    return typeof params.formattedValue === 'string' || typeof params.formattedValue === 'number'
      ? String(params.formattedValue)
      : typeof params.value === 'string' || typeof params.value === 'number'
      ? String(params.value)
      : resolvedQaValue.text;
  };

const formatTableExportValue = ({
  value,
  column,
  augmentation,
}: {
  value: unknown;
  column?: TableColumn;
  augmentation?: StudyReportTableAugmentation;
}): string => {
  if (column && augmentation) {
    const resolvedQaValue = getResolvedQaValue({ column, value, augmentation });
    if (resolvedQaValue?.text) {
      return resolvedQaValue.text;
    }
  }

  const normalizedLink = normalizeTableLinkValue(value);
  if (normalizedLink) {
    return normalizedLink.href;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (isPrimitiveCellValue(value)) {
    return String(value);
  }

  if (value === null || value === undefined) {
    return '';
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const escapeCsvCell = (value: string) => `"${value.replace(CSV_QUOTE_REGEX, '""')}"`;

const inferColumnsFromRows = (rows: Record<string, unknown>[]): TableColumn[] => {
  if (rows.length === 0) {
    return [];
  }

  const fieldSet = new Set<string>();
  for (const row of rows) {
    for (const key of keys(row)) {
      fieldSet.add(key);
    }
  }

  return Array.from(fieldSet)
    .filter((field) => !ROW_IDENTIFIER_FIELD_NAMES.includes(field))
    .map((field) => {
      const sampleValue = rows.find((row) => row[field] != null)?.[field];
      const inferredType: TableColumn['type'] =
        typeof sampleValue === 'number' ? 'number' : typeof sampleValue === 'boolean' ? 'boolean' : 'string';

      return {
        field,
        headerName: field,
        type: inferredType,
      };
    });
};

const resolveTableColumns = (table: SectionTable): TableColumn[] => {
  if (table.columns.length > 0) {
    return table.columns;
  }

  return inferColumnsFromRows(table.rows as Record<string, unknown>[]);
};

export const buildStudyReportTableColumns = (
  table: SectionTable,
  augmentation?: StudyReportTableAugmentation,
  viewerLinkResolver?: StudyReportViewerLinkResolver
): GridColDef[] =>
  map(
    filter(resolveTableColumns(table), (column) => !shouldHideIndexColumn(table, column)),
    (column) => {
      const valueOptions = resolveCategoricalValueOptions(table, column, augmentation);
      const hasLinkValues = some(table.rows, (row) =>
        Boolean(normalizeTableLinkValue((row as Record<string, unknown>)[column.field]))
      );
      const hasQaValueAugmentation = Boolean(
        (column.meta || augmentation?.columnMetaByField[column.field]) && augmentation
      );
      const hasSlidePreviewValues =
        isSlidePreviewColumn(column) &&
        some(table.rows, (row) => {
          const rawValue = (row as Record<string, unknown>)[column.field];
          return getSlidePreviewIds({ rawValue, column }).length > 0;
        });
      const hasViewerLinkValues =
        Boolean(viewerLinkResolver) &&
        some(table.rows, (row) =>
          Boolean(
            viewerLinkResolver?.resolveCellLink({
              column,
              field: column.field,
              row: row as Record<string, unknown>,
              value: (row as Record<string, unknown>)[column.field],
            })
          )
        );
      const resolvedType =
        column.type === 'singleSelect' || ((column.type === 'string' || !column.type) && valueOptions)
          ? 'singleSelect'
          : column.type || 'string';
      const description =
        compact([column.description, column.unit ? `Unit: ${column.unit}` : null]).join(' • ') || undefined;

      return {
        field: column.field,
        headerName: column.unit ? `${column.headerName} (${column.unit})` : column.headerName,
        description,
        type: resolvedType,
        sortable: column.sortable !== false,
        filterable: column.filterable !== false,
        filterOperators:
          resolvedType === 'date' || resolvedType === 'dateTime' ? getReadOnlyDateTimeFilterOperators() : undefined,
        width: column.width || 160,
        flex: column.width ? undefined : 1,
        align: column.align,
        headerAlign: column.align,
        valueOptions,
        valueGetter: buildColumnValueGetter(column, augmentation),
        ...(hasLinkValues
          ? { renderCell: buildLinkCellRenderer(column.field) }
          : hasViewerLinkValues
          ? {
              renderCell: buildViewerLinkCellRenderer({
                field: column.field,
                column,
                augmentation,
                viewerLinkResolver: viewerLinkResolver!,
              }),
            }
          : hasSlidePreviewValues
          ? { renderCell: buildFallbackSlidePreviewCellRenderer({ field: column.field, column, augmentation }) }
          : hasQaValueAugmentation
          ? { renderCell: buildAugmentedCellRenderer({ field: column.field, column, augmentation: augmentation! }) }
          : {}),
      } as GridColDef;
    }
  );

export const buildStudyReportAutosizedColumns = (
  table: SectionTable,
  augmentation?: StudyReportTableAugmentation,
  viewerLinkResolver?: StudyReportViewerLinkResolver
): GridColDef[] =>
  map(buildStudyReportTableColumns(table, augmentation, viewerLinkResolver), (column) => ({
    ...column,
    flex: undefined,
  }));

export const buildStudyReportTableAutosizeOptions = (
  table: SectionTable,
  augmentation?: StudyReportTableAugmentation,
  viewerLinkResolver?: StudyReportViewerLinkResolver
): GridAutosizeOptions => ({
  columns: map(buildStudyReportTableColumns(table, augmentation, viewerLinkResolver), 'field'),
  includeHeaders: true,
});

export const buildStudyReportTableRows = (table: SectionTable) => {
  const preferredRowIdField = resolveStableRowIdField(table);

  return map(table.rows, (row, index) => {
    const rawRow = row as Record<string, unknown>;
    const rowId = rawRow.id ?? (preferredRowIdField ? rawRow[preferredRowIdField] : undefined);

    return {
      id: isString(rowId) || isNumber(rowId) ? rowId : `${table.id}-${index}`,
      ...rawRow,
    };
  });
};

export const buildStudyReportTableExportFileName = (table: SectionTable) =>
  kebabCase(table.title || `${table.sourceType} table`) || 'report-table';

export const buildStudyReportTableCsvContent = (
  table: SectionTable,
  rows: Array<Record<string, unknown>> = buildStudyReportTableRows(table) as Array<Record<string, unknown>>,
  augmentation?: StudyReportTableAugmentation
) => {
  const columns = buildStudyReportTableColumns(table, augmentation);
  const resolvedColumns = resolveTableColumns(table);

  const csvRows = [
    map(columns, (column) => column.headerName || column.field),
    ...map(rows, (row) =>
      map(columns, (column) =>
        formatTableExportValue({
          value: (row as Record<string, unknown>)[column.field],
          column: resolvedColumns.find((tableColumn) => tableColumn.field === column.field),
          augmentation,
        })
      )
    ),
  ];

  return map(csvRows, (cells) => map(cells, escapeCsvCell).join(',')).join('\n');
};
